import React from 'react';
import './PrintableNewPriceList.css'; // CSS for styling the cards

interface Structure {
  id: number;
  code: string;
  type: string;
  name: string;
  address: string;
  size: string;
  currentDailyPrice: number | null;
  currentMonthlyPrice: number | null;
  imageUrl?: string;
  // images?: string[]; // Removed as per user feedback
  description?: string;
  latitude?: number | null; // Can be null if not parsable
  longitude?: number | null; // Can be null if not parsable
}

interface PrintableNewPriceListProps {
  structures: Structure[];
  companyName: string;
  companyDetails: string;
  currentDate: string;
}

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null || isNaN(value)) return 'По запросу';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const isCityboard = (type: string) => {
  return type === 'Ситиборд';
};

// Updated MapPlaceholder to show a static map image placeholder
// Actual static map image generation would require a backend service or third-party API
const StaticMapPlaceholder: React.FC<{ items: Structure[] }> = ({ items }) => {
  // Create a list of markers for the map
  // Example: &markers=color:blue%7Clabel:S%7C40.702147,-74.015794&markers=color:green%7Clabel:G%7C40.711614,-74.012318
  // This is highly dependent on the chosen static map API.
  // For now, just a placeholder div.
  const validItemsWithCoords = items.filter(item => typeof item.latitude === 'number' && typeof item.longitude === 'number');

  if (validItemsWithCoords.length === 0) {
    return <div className="static-map-placeholder">Карта недоступна (нет координат)</div>;
  }
  
  // Placeholder text indicating what would be here.
  // In a real implementation, this would be an <img> tag with a URL to a static map service.
  const markerInfo = validItemsWithCoords.map(item => `${item.code}: ${item.latitude?.toFixed(4)},${item.longitude?.toFixed(4)}`).join('; ');
  
  return (
    <div className="static-map-placeholder">
      <p style={{fontWeight: 'bold', marginBottom: '5px'}}>Карта с локациями:</p>
      <div style={{border: '1px dashed #ccc', padding: '10px', backgroundColor: '#f9f9f9', textAlign: 'center'}}>
        [Статическое изображение карты с метками для: {markerInfo}]
        <br />
        <small>(Требуется интеграция с сервисом статических карт)</small>
      </div>
    </div>
  );
};

// Updated ImageGalleryPlaceholder to handle multiple images for combined groups
interface ImageGalleryItem {
  imageUrl?: string;
  code: string; // For caption
  altText: string;
}

const ImageGalleryPlaceholder: React.FC<{ items: ImageGalleryItem[]; isCombined: boolean }> = ({ items, isCombined }) => {
  if (!items || items.length === 0) {
    return <div className="image-gallery-placeholder no-image">Нет изображений</div>;
  }

  if (!isCombined && items[0]) { // Single item display
    const item = items[0];
    if (!item.imageUrl) {
      return <div className="image-gallery-placeholder no-image">Нет изображения для {item.code}</div>;
    }
    return (
      <div className="image-gallery-placeholder single-image-container">
        <figure>
          <img src={item.imageUrl} alt={item.altText} className="primary-image" />
          <figcaption className="image-caption">Код: {item.code}</figcaption>
        </figure>
      </div>
    );
  }

  // Combined items display (multiple images)
  return (
    <div className="image-gallery-placeholder combined-images-container">
      {items.map((item, index) => (
        item.imageUrl ? (
          <figure key={index} className="combined-image-item">
            <img src={item.imageUrl} alt={item.altText} className="combined-image" />
            <figcaption className="image-caption">Код: {item.code}</figcaption>
          </figure>
        ) : (
          <div key={index} className="combined-image-item no-image-item">
            Нет изображения для {item.code}
          </div>
        )
      ))}
    </div>
  );
};


const PrintableNewPriceList: React.FC<PrintableNewPriceListProps> = ({
  structures,
  companyName,
  companyDetails,
  currentDate,
}) => {
  if (!structures || structures.length === 0) {
    return <div className="printable-container p-8 text-center">Нет конструкций для отображения в прайс-листе.</div>;
  }

  // Group structures by type and then by price to combine similar ones
  const groupedAndCombinedStructures = structures.reduce((acc, structure) => {
    const typeKey = structure.type || 'Uncategorized';
    if (!acc[typeKey]) {
      acc[typeKey] = {};
    }
    // Create a composite key for price combination
    const priceKey = `${structure.currentDailyPrice}-${structure.currentMonthlyPrice}`;
    if (!acc[typeKey][priceKey]) {
      acc[typeKey][priceKey] = {
        type: structure.type,
        dailyPrice: structure.currentDailyPrice,
        monthlyPrice: structure.currentMonthlyPrice,
        // Take details from the first structure in the group
        // This might need refinement if details vary significantly within a price group
        name: structure.name, // This will be overridden if multiple structures
        address: structure.address, // This will be overridden if multiple structures
        size: structure.size, // This will be overridden if multiple structures
        imageUrl: structure.imageUrl,
        // images: structure.images, // Removed
        description: structure.description,
        latitude: structure.latitude,
        longitude: structure.longitude,
        combinedItems: [], // Store original items
      };
    }
    acc[typeKey][priceKey].combinedItems.push(structure);
    return acc;
  }, {} as Record<string, Record<string, {
    type: string;
    dailyPrice: number | null;
    monthlyPrice: number | null;
    name: string;
    address: string;
    size: string;
    imageUrl?: string;
    // images?: string[]; // Removed
    description?: string;
    latitude?: number | null;
    longitude?: number | null;
    combinedItems: Structure[];
  }>>);


  return (
    <div className="printable-new-price-list">
      <header className="price-list-header"> {/* Removed print:break-after-page */}
        <h1>{companyName}</h1>
        <p>{companyDetails}</p>
        <h2>Прайс-лист на рекламные конструкции</h2>
        <p>Действителен на: {currentDate}</p>
      </header>

      {Object.values(groupedAndCombinedStructures).map((priceGroups, typeIndex) => (
        <React.Fragment key={typeIndex}>
          {Object.values(priceGroups).map((group, groupIndex) => {
            const isCombined = group.combinedItems.length > 1;
            const primaryStructure = group.combinedItems[0]; // Use first item for common details
            const isCityboardType = isCityboard(group.type);

            return (
              <div key={`${typeIndex}-${groupIndex}`} className="structure-card print:break-inside-avoid-page">
                <div className="card-image-section">
                  <ImageGalleryPlaceholder
                    items={
                      isCombined
                        ? group.combinedItems.map(ci => ({ imageUrl: ci.imageUrl, code: ci.code, altText: `Конструкция ${ci.code}` }))
                        : [{ imageUrl: primaryStructure.imageUrl, code: primaryStructure.code, altText: primaryStructure.name }]
                    }
                    isCombined={isCombined}
                  />
                </div>
                <div className="card-details-section">
                  <h3 className="card-title">
                    {isCombined ? `Конструкции типа "${group.type}" (${group.combinedItems.length} шт.)` : primaryStructure.name}
                  </h3>
                  {!isCombined && <p className="card-code">Код: {primaryStructure.code}</p>}
                  
                  <div className="card-info-grid">
                    <div>
                      <p className="card-label">Тип:</p>
                      <p className="card-value">{group.type}</p>
                    </div>
                    <div>
                      <p className="card-label">Размер:</p>
                      <p className="card-value">{group.size || primaryStructure.size || '-'}</p>
                    </div>
                  </div>

                  {isCombined ? (
                    <div className="combined-details-section">
                      <p className="card-label">Адреса ({group.combinedItems.length}):</p>
                      <ul className="address-list">
                        {group.combinedItems.map(item => <li key={item.id}><strong>{item.code}:</strong> {item.address}</li>)}
                      </ul>
                    </div>
                  ) : (
                    <>
                      <p className="card-label">Адрес:</p>
                      <p className="card-value">{primaryStructure.address}</p>
                    </>
                  )}

                  {isCombined ? (
                    group.combinedItems.some(item => item.description) && (
                      <div className="combined-details-section">
                        <p className="card-label">Описания:</p>
                        <ul className="description-list">
                          {group.combinedItems.map(item =>
                            item.description ? <li key={item.id}><strong>{item.code}:</strong> {item.description}</li> : null
                          )}
                        </ul>
                      </div>
                    )
                  ) : (
                    primaryStructure.description && (
                      <>
                        <p className="card-label">Описание:</p>
                        <p className="card-description">{primaryStructure.description}</p>
                      </>
                    )
                  )}

                  <div className="card-pricing">
                    {!isCityboardType && (
                      <p>Цена/день: <span className="price-value">{formatCurrency(group.dailyPrice)}</span></p>
                    )}
                    <p>Цена/месяц: <span className="price-value">{formatCurrency(group.monthlyPrice)}</span></p>
                  </div>
                  
                  <StaticMapPlaceholder items={group.combinedItems} />
                </div>
              </div>
            );
          })}
        </React.Fragment>
      ))}
      
      <footer className="price-list-footer">
        <p>Цены указаны в тенге (KZT) с учетом НДС и действительны на {currentDate}.</p>
        <p>Для получения подробной информации и актуальных условий, пожалуйста, свяжитесь с нашими менеджерами.</p>
        <p>{companyName} | {companyDetails}</p>
      </footer>
    </div>
  );
};

export default PrintableNewPriceList;
